import { Link } from '@tanstack/react-router';
import { useFraudHolds } from '../../lib/api/hooks-fraud.js';
import { useSession } from '../../lib/auth/session.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * A fraud hold, where somebody will look for why calls are failing (docs/39): on the extension
 * itself, and on the account's front page. Only while a hold is live -- banners are for problems.
 *
 * `extension` given: holds on that seat or the whole account. Omitted: any hold on the account.
 * The client is told who releases it; the MSP gets a link to where it is released.
 */
export function FraudHoldBanner({ tenantId, extension }: { tenantId: string; extension?: string | undefined }) {
  const can = useSession((s) => s.can);
  const holds = useFraudHolds({ tenant: tenantId }, !!tenantId && can('fraud:read'));
  const live = (holds.data?.data ?? []).filter((h) =>
    extension === undefined || h.extension_number === null || h.extension_number === extension);
  if (live.length === 0) return null;

  const account = live.find((h) => h.extension_number === null);
  const text = account
    ? `Chargeable calls are held on this whole account: ${account.reason}.`
    : extension !== undefined
      ? `Chargeable calls from this extension are held: ${live[0]!.reason}.`
      : `Chargeable calls are held on ${live.length === 1 ? `extension ${live[0]!.extension_number}` : `${live.length} extensions (${live.map((h) => h.extension_number).join(', ')})`}.`;

  return (
    <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }}>
      <Icon name="warning" size={14} />
      <span>
        {text} Internal and emergency calls still work.{' '}
        {can('fraud:write')
          ? <Link to="/fraud">Review and release</Link>
          : 'If these calls were not yours, change the extension’s password; your service provider releases the hold.'}
      </span>
    </div>
  );
}
